/**
 * English Language Profile
 *
 * SVO word order, prepositions, space-separated.
 */

import type { LanguageProfile } from './types';

export const englishProfile: LanguageProfile = {
  code: 'en',
  name: 'English',
  nativeName: 'English',
  regions: ['western', 'priority'],
  direction: 'ltr',
  script: 'latin',
  wordOrder: 'SVO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  // English infinitive and imperative are identical ("toggle", "save", "open")
  defaultVerbForm: 'base',
  verb: {
    position: 'start',
    subjectDrop: false,
  },
  references: {
    me: 'me',
    it: 'it',
    you: 'you',
    result: 'result',
    event: 'event',
    target: 'target',
    body: 'body',
    document: 'document',
    window: 'window',
    detail: 'detail',
  },
  possessive: {
    marker: "'s",
    markerPosition: 'after-object',
    specialForms: {
      me: 'my',
      it: 'its',
      you: 'your',
    },
    keywords: {
      my: 'me',
      your: 'you',
      its: 'it',
    },
  },
  roleMarkers: {
    destination: { primary: 'on', alternatives: ['to', 'from'], position: 'before' },
    source: { primary: 'from', position: 'before' },
    patient: { primary: '', position: 'before' },
    style: { primary: 'with', alternatives: ['by', 'using'], position: 'before' },
    responseType: { primary: 'as', position: 'before' },
    method: { primary: 'via', alternatives: ['using'], position: 'before' },
  },
  keywords: {
    toggle: { primary: 'toggle' },
    add: { primary: 'add' },
    remove: { primary: 'remove' },
    put: { primary: 'put' },
    append: { primary: 'append' },
    prepend: { primary: 'prepend' },
    take: { primary: 'take' },
    make: { primary: 'make' },
    clone: { primary: 'clone' },
    swap: { primary: 'swap' },
    morph: { primary: 'morph' },
    set: { primary: 'set' },
    get: { primary: 'get' },
    increment: { primary: 'increment' },
    decrement: { primary: 'decrement' },
    log: { primary: 'log' },
    show: { primary: 'show' },
    hide: { primary: 'hide' },
    transition: { primary: 'transition' },
    on: { primary: 'on' },
    trigger: { primary: 'trigger' },
    send: { primary: 'send' },
    focus: { primary: 'focus' },
    blur: { primary: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'empty', normalized: 'empty' },
    open: { primary: 'open', normalized: 'open' },
    close: { primary: 'close', normalized: 'close' },
    select: { primary: 'select', normalized: 'select' },
    clear: { primary: 'clear', normalized: 'clear' },
    reset: { primary: 'reset', normalized: 'reset' },
    breakpoint: { primary: 'breakpoint', normalized: 'breakpoint' },
    go: { primary: 'go' },
    scroll: { primary: 'scroll', normalized: 'scroll' },
    push: { primary: 'push', normalized: 'push' },
    replace: { primary: 'replace', normalized: 'replace' },
    process: { primary: 'process', normalized: 'process' },
    wait: { primary: 'wait' },
    fetch: { primary: 'fetch' },
    settle: { primary: 'settle' },
    if: { primary: 'if' },
    when: { primary: 'when', normalized: 'when' },
    // Reactive `when <expr> changes` trigger word — synced VERBATIM from the i18n
    // dictionary (`changes`), which is what wrote every stored corpus row; the V1
    // vocab gate requires the two surfaces to agree. Native review pending — see
    // NATIVE_REVIEW_NEEDED.md § "Reactive `when … changes`".
    changes: { primary: 'changes', normalized: 'changes' },
    where: { primary: 'where', normalized: 'where' },
    else: { primary: 'else' },
    repeat: { primary: 'repeat' },
    for: { primary: 'for' },
    while: { primary: 'while' },
    continue: { primary: 'continue' },
    halt: { primary: 'halt' },
    throw: { primary: 'throw' },
    call: { primary: 'call' },
    return: { primary: 'return' },
    then: { primary: 'then' },
    and: { primary: 'and' },
    end: { primary: 'end' },
    js: { primary: 'js' },
    async: { primary: 'async' },
    tell: { primary: 'tell' },
    default: { primary: 'default' },
    init: { primary: 'init' },
    behavior: { primary: 'behavior' },
    install: { primary: 'install' },
    measure: { primary: 'measure' },
    beep: { primary: 'beep' },
    ask: { primary: 'ask' },
    answer: { primary: 'answer' },
    break: { primary: 'break' },
    copy: { primary: 'copy' },
    exit: { primary: 'exit' },
    pick: { primary: 'pick' },
    render: { primary: 'render' },
    into: { primary: 'into' },
    before: { primary: 'before' },
    after: { primary: 'after' },
    until: { primary: 'until' },
    event: { primary: 'event' },
    from: { primary: 'from' },
    // Phase 8 (htmx v4 localized attributes): keywords used as attribute
    // suffixes by the vocab generator — `sse-connect`, `hx-live`, etc.
    connect: { primary: 'connect', normalized: 'connect' },
    stream: { primary: 'stream', normalized: 'stream' },
    live: { primary: 'live', normalized: 'live' },
    socket: { primary: 'socket', alternatives: ['websocket'], normalized: 'socket' },
    bind: { primary: 'bind', normalized: 'bind' },
    eventsource: { primary: 'eventsource', normalized: 'eventsource' },
    worker: { primary: 'worker', normalized: 'worker' },
    intercept: { primary: 'intercept', normalized: 'intercept' },
  },
};
